import apiV2 from "api-v2";

export interface QuoteTemplateColumnPayload {
	column_type: string;
	label: string;
	sort_order: number;
}

export interface QuoteTemplateRowPayload {
	sort_order: number;
	unit_id: number | null;
	/**
	 * Procurer-filled values for every Description column, keyed by the
	 * destination column's `sort_order`. The primary description column's
	 * value lives here too — there is no separate `name` field on rows
	 * (cells-only refactor).
	 */
	cells: Record<number, string>;
}

export interface SaveQuoteTemplatePayload {
	name: string;
	description?: string | null;
	quote_table_name?: string | null;
	columns: QuoteTemplateColumnPayload[];
	rows: QuoteTemplateRowPayload[];
}

export interface QuoteTemplateColumn {
	id: number;
	column_type: string;
	label: string;
	sort_order: number;
}

export interface QuoteTemplateRow {
	id: number;
	sort_order: number;
	unit_id: number | null;
	unit_name?: string;
	/** All description-column values, keyed by `sort_order`. */
	cells: Record<number, string>;
}

export interface QuoteTemplate {
	id: number;
	name: string;
	description?: string | null;
	quote_table_name?: string | null;
	columns: QuoteTemplateColumn[];
	created_at?: string;
	rows: QuoteTemplateRow[];
	updated_at?: string;
}

const TEMPLATES_PATH = "rfx-settings/quote-templates";

interface RawTemplateColumn {
	name: string;
	sort_order: number;
	type: string;
}

interface RawTemplateRow {
	/**
	 * Legacy field — older stored templates persisted the primary description
	 * value here. New writes never include it; `normalizeTemplate` migrates
	 * any incoming `name` into `cells` under the primary description column's
	 * sort_order so old templates still render correctly.
	 */
	name?: string;
	sort_order: number;
	unit_id: number | null;
	unit_name?: string;
	cells?: Record<string, string>;
}

interface RawQuoteTemplate {
	id: number;
	name: string;
	description?: string | null;
	quote_table_name?: string | null;
	created_at?: string;
	structure: {
		columns: RawTemplateColumn[];
		rows: RawTemplateRow[];
	};
	updated_at?: string;
}

const normalizeTemplate = (raw: RawQuoteTemplate): QuoteTemplate => {
	const columns = (raw.structure?.columns || []).map((c, idx) => ({
		id: idx,
		column_type: c.type,
		label: c.name,
		sort_order: c.sort_order,
	}));
	// Locate the primary description column so we can fold any legacy
	// per-row `name` value into cells keyed by its sort_order.
	const primaryDescSortOrder = columns
		.filter((c) => c.column_type === "description")
		.sort((a, b) => a.sort_order - b.sort_order)[0]?.sort_order;

	const rows = (raw.structure?.rows || []).map((r, idx) => {
		// Cells come back from JSON storage with string keys — normalise to numeric.
		const rawCells = r.cells ?? {};
		const cells: Record<number, string> = {};
		for (const k of Object.keys(rawCells)) {
			cells[Number(k)] = rawCells[k];
		}
		// Backwards compat: migrate legacy `r.name` into the primary
		// description column's cell. Cell value (if already set) takes
		// precedence — it represents a newer write.
		if (
			r.name !== undefined &&
			r.name !== "" &&
			primaryDescSortOrder !== undefined &&
			cells[primaryDescSortOrder] === undefined
		) {
			cells[primaryDescSortOrder] = r.name;
		}
		return {
			id: idx,
			unit_id: r.unit_id,
			unit_name: r.unit_name,
			sort_order: r.sort_order,
			cells,
		};
	});

	return {
		id: raw.id,
		name: raw.name,
		description: raw.description ?? null,
		quote_table_name: raw.quote_table_name ?? null,
		columns,
		rows,
		created_at: raw.created_at,
		updated_at: raw.updated_at,
	};
};

export const getQuoteTemplates = async (): Promise<QuoteTemplate[]> => {
	const response = await apiV2.get<{ data: RawQuoteTemplate[] }>(TEMPLATES_PATH);
	return response.data.data.map(normalizeTemplate);
};

export const getQuoteTemplate = async (
	templateId: number
): Promise<QuoteTemplate> => {
	const response = await apiV2.get<{ data: RawQuoteTemplate }>(
		`${TEMPLATES_PATH}/${templateId}`
	);
	return normalizeTemplate(response.data.data);
};

// The backend (StoreQuoteTemplateRequest) expects a nested `structure` object
// with columns shaped as { name, type, sort_order } and rows as
// { name, unit_id, sort_order }. The frontend works in `{ label, column_type }`
// terms, so translate at the API boundary.
const toWirePayload = (payload: SaveQuoteTemplatePayload) => ({
	name: payload.name,
	description: payload.description ?? null,
	quote_table_name: payload.quote_table_name ?? null,
	structure: {
		columns: payload.columns.map((c) => ({
			name: c.label,
			type: c.column_type,
			sort_order: c.sort_order,
		})),
		rows: payload.rows.map((r) => ({
			unit_id: r.unit_id,
			sort_order: r.sort_order,
			cells: r.cells,
		})),
	},
});

export const createQuoteTemplate = async (
	payload: SaveQuoteTemplatePayload
): Promise<QuoteTemplate> => {
	const response = await apiV2.post<{ data: RawQuoteTemplate }>(
		TEMPLATES_PATH,
		toWirePayload(payload)
	);
	return normalizeTemplate(response.data.data);
};

export const updateQuoteTemplate = async (
	templateId: number,
	payload: SaveQuoteTemplatePayload
): Promise<QuoteTemplate> => {
	const response = await apiV2.put<{ data: RawQuoteTemplate }>(
		`${TEMPLATES_PATH}/${templateId}`,
		toWirePayload(payload)
	);
	return normalizeTemplate(response.data.data);
};

export const deleteQuoteTemplate = async (
	templateId: number
): Promise<void> => {
	await apiV2.delete(`${TEMPLATES_PATH}/${templateId}`);
};
